import { colors, entities } from "@fraction/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { ReactNode, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import { logout } from "src/auth/mutations";
import { PasswordInput, TextInput, UnauthenticatedLayout } from "src/components";
import { Variant } from "src/components/TextInput";
import { Button } from "src/components/ui/button";
import { RegularText } from "src/components/v1";
import { useFormReducer } from "src/hooks";
import { createStyles } from "src/styles";
import { z } from "zod";

export interface RegisterLayoutProps {
  onSubmit?: (submission?: Record<string, string>) => void;
  user?: entities.User;
  isLoading?: boolean;
  className?: string;
  hideHelperButtons?: boolean;
}

const styles = createStyles({
  input: {
    width: "100%",
  },
  flexEnd: {
    display: "flex",
    justifyContent: "flex-end",
  },
  login: {
    marginTop: 32,
    width: "75%",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: 32,
  },
  helperText: {
    fontSize: 14,
    color: colors.palette.GREY_500,
    marginBottom: 4,
  },
  hasAccount: {
    marginTop: 6,
    fontSize: 14,
    color: colors.palette.GREEN,
  },
  neverMind: {
    marginTop: 6,
    fontSize: 14,
    color: colors.palette.RED,
  },
  link: {
    textDecoration: "none",
  },
});

const LOGIN = z
  .object({
    password: z.string().min(8),
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    path: ["confirm"],
  });

const HelperText = ({ children, error }: { children: ReactNode; error?: boolean }) => (
  <RegularText style={[styles.helperText, error && { color: colors.ERROR }]}>{children}</RegularText>
);

const RegisterLayout = (props: RegisterLayoutProps) => {
  return (
    <UnauthenticatedLayout
      header={
        <>
          Welcome to Fraction!
          <br />
          Register your email to continue
        </>
      }
    >
      <RegisterInnerLayout {...props} />
    </UnauthenticatedLayout>
  );
};

export const RegisterInnerLayout = ({
  user,
  isLoading,
  className,
  hideHelperButtons,
  ...props
}: RegisterLayoutProps) => {
  const { formValues, handleTextChange } = useFormReducer({
    initialValues: {
      password: "",
      confirm: "",
    },
  });

  const { register, errors, handleSubmit } = useForm({
    resolver: zodResolver(LOGIN),
  });

  const onSubmit = useCallback(() => {
    props.onSubmit?.({ password: formValues.password });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onSubmit, formValues.password]);

  const location = useLocation();
  const onClickNeverMind = useCallback(() => {
    logout().then(() => {
      window.location.href = location?.state?.from || "/";
    });
  }, []);

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <div>
        <TextInput
          clearable={false}
          style={styles.input}
          defaultValue={user?.email}
          disabled
          variant={Variant.V2}
        />
        <PasswordInput
          style={styles.input}
          autoComplete="new-password"
          defaultValue={formValues.password}
          onChange={handleTextChange}
          ref={register}
        />
        <PasswordInput
          style={styles.input}
          name="confirm"
          label="Confirm Password"
          autoComplete="new-password"
          defaultValue={formValues.confirm}
          onChange={handleTextChange}
          ref={register}
        />
      </div>
      <HelperText error={errors?.password?.type === "too_small"}>
        Password must be more than 8 characters long.
      </HelperText>
      <div css={styles.actions}>
        <Button
          type="submit"
          className="rounded-full w-full max-w-[200px] mt-4"
          variant="green"
          pending={isLoading}
        >
          Register
        </Button>
        {!hideHelperButtons ? (
          <>
            <Link to="/login" css={styles.link}>
              <Button className="text-gray-600 mt-1" variant="ghost">
                Already have an account?
              </Button>
            </Link>
            <Button className="text-gray-600 mt-1" onClick={onClickNeverMind} variant="ghost">
              Never mind
            </Button>
          </>
        ) : null}
      </div>
    </form>
  );
};

export default React.memo(RegisterLayout);
